'use client'

import { type PropsWithChildren, useEffect } from 'react'

import { HeaderLayout } from '@/components/layout'
import { SceneID } from '@/enums/agent'
import { registerMiniSoraForceNewTab } from '@/lib/config/minisora-agent'
import { presetState, resetPresetState, subscribePresetState } from '@/lib/preset-state'
import { useImagesStore } from '@/stores/images'
import { useModelStore } from '@/stores/model'
import { useParamsStore } from '@/stores/params'

/** 同步写入 store，确保 SSR 与首屏 CSR 拿到同一个 lockedModelId */
const useLockMiniSora = () => {
  if (useModelStore.getState().lockedModelId !== SceneID.SORA2) {
    useModelStore.getState().setLockedModelId(SceneID.SORA2)
  }

  useEffect(() => {
    useModelStore.getState().setLockedModelId(SceneID.SORA2)
    return () => {
      useModelStore.getState().setLockedModelId(null)
    }
  }, [])
}

/** MiniSora 不支持玩法模式，进入时清掉 explore 残留的玩法选中 */
const useClearToolMode = () => {
  useEffect(() => {
    const paramsStore = useParamsStore.getState()
    if (paramsStore.selectedToolMode !== 'default') {
      paramsStore.setSelectedToolMode('default')
    }
  }, [])
}

/** 首页模板图是异步准备的，切页过快会在清空之后才写入，这段时间内持续拦截 */
const LATE_TEMPLATE_GUARD_MS = 1000

/** 本页上传的图片带真实文件；模板槽位的 file 为空或 size 为 0 */
const isUploadedOnPage = (slot: { file?: File }) => !!slot.file && slot.file.size > 0

/**
 * 进入时输入框必须为空：清掉 explore 带来的模板、图片和提示词。
 * 提交会把所有成功的图片槽交给 Agent，只隐藏不清除会把模板图一起带过去。
 * images / params store 都是持久化的，需等两者水合完成再清，否则会被恢复回来
 */
const useClearCarriedInput = () => {
  useEffect(() => {
    let started = false
    let guardTimer: ReturnType<typeof setTimeout> | undefined
    const cleanups: Array<() => void> = []

    const clearAll = () => {
      resetPresetState()
      useImagesStore.getState().resetImagesKeepMode()
      useParamsStore.getState().setVideoPrompt('')
    }

    const startGuard = () => {
      const unsubscribePreset = subscribePresetState(() => {
        if (!presetState.selectedPreset) return
        resetPresetState()
        useParamsStore.getState().setVideoPrompt('')
      })
      const unsubscribeImages = useImagesStore.subscribe(state => {
        if (state.imageSlots.every(isUploadedOnPage)) return
        useImagesStore.setState({ imageSlots: state.imageSlots.filter(isUploadedOnPage) })
      })
      const stopGuard = () => {
        unsubscribePreset()
        unsubscribeImages()
      }
      cleanups.push(stopGuard)
      guardTimer = setTimeout(stopGuard, LATE_TEMPLATE_GUARD_MS)
    }

    const tryStart = () => {
      if (started) return
      if (!useImagesStore.getState()._hasHydrated || !useParamsStore.getState()._hasHydrated) return
      started = true
      clearAll()
      startGuard()
    }

    tryStart()
    if (!started) {
      cleanups.push(useImagesStore.subscribe(tryStart), useParamsStore.subscribe(tryStart))
    }

    return () => {
      clearTimeout(guardTimer)
      cleanups.forEach(cleanup => cleanup())
    }
  }, [])
}

export const MiniSoraLayoutClient = ({ children }: PropsWithChildren) => {
  useLockMiniSora()
  useClearToolMode()
  useClearCarriedInput()
  useEffect(() => registerMiniSoraForceNewTab(), [])

  return (
    <div className="relative h-full overflow-hidden">
      <HeaderLayout />
      {children}
    </div>
  )
}
